import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Magnet, XCircle } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const OUTCOMES = ["open", "attached", "declined", "follow_up", "ineligible"];
const OUTCOME_VARIANTS = {
  open: "border-amber-500/40 text-amber-300",
  attached: "border-emerald-500/40 text-emerald-300",
  declined: "border-rose-500/40 text-rose-300",
  follow_up: "border-sky-500/40 text-sky-300",
  ineligible: "border-zinc-600 text-zinc-400",
};

function UpdateDialog({ event, onSaved, onClose }) {
  const [outcome, setOutcome] = useState(event.outcome || "open");
  const [notes, setNotes] = useState(event.notes || "");
  const [followUp, setFollowUp] = useState(event.follow_up_at || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await crmApi.updateAttach(event.id, { outcome, notes, follow_up_at: followUp || null });
      toast.success("Attach event updated");
      onSaved?.();
      onClose();
    } catch (exc) {
      toast.error(exc.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader><DialogTitle>Update attach · {event.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="attach-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Follow up at (ISO date)</Label>
            <Input value={followUp || ""} onChange={(e) => setFollowUp(e.target.value)} placeholder="2026-07-01" className="bg-zinc-950 border-zinc-700 text-white" data-testid="attach-followup" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} className="text-zinc-300">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="save-attach">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AttachView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState("open");
  const [editEvent, setEditEvent] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await crmApi.listAttach(outcomeFilter || undefined);
      setEvents(list || []);
    } catch (exc) {
      toast.error(exc.message || "Failed to load attach events");
    } finally {
      setLoading(false);
    }
  }, [outcomeFilter]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await crmApi.listAttach(outcomeFilter || undefined);
        if (active) setEvents(list || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load attach events");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [outcomeFilter]);

  const quick = async (event, outcome) => {
    try {
      await crmApi.updateAttach(event.id, { outcome });
      toast.success(`Marked ${outcome}`);
      reload();
    } catch (exc) {
      toast.error(exc.message || "Update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-attach">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Magnet className="h-5 w-5 text-amber-300" /> Attach Flywheel</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Auto-generated when a CRM project closes. Convert finished customers into plan members. Phase D will nurture these via Jordan/Taylor sequences.
          </p>
        </div>
        <Select value={outcomeFilter || "all"} onValueChange={(v) => setOutcomeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 bg-zinc-950 border-zinc-700 text-white" data-testid="attach-outcome-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : events.length === 0 ? (
        <Card className="bg-black/40 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            No attach events for this filter. They’re created automatically when a project transitions to “closed”.
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="attach-grid">
          {events.map((e) => (
            <Card key={e.id} className="bg-black/40 border-zinc-800" data-testid={`attach-card-${e.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-white font-semibold">{e.customer_name}</div>
                    <div className="text-[11px] text-zinc-500">{e.customer_email || "no email"}</div>
                  </div>
                  <Badge variant="outline" className={OUTCOME_VARIANTS[e.outcome] || "border-zinc-700 text-zinc-300"}>{e.outcome}</Badge>
                </div>
                <div className="text-xs text-zinc-300">Suggest <span className="text-amber-300">{e.suggested_tier}</span> tier</div>
                {e.notes && <div className="text-xs text-zinc-400 whitespace-pre-wrap line-clamp-3">{e.notes}</div>}
                <div className="text-[11px] text-zinc-500">Created {new Date(e.created_at).toLocaleDateString()}</div>
                {e.outcome === "open" && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" onClick={() => quick(e, "attached")} data-testid={`mark-attached-${e.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Attached
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" onClick={() => quick(e, "declined")} data-testid={`mark-declined-${e.id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Declined
                    </Button>
                    <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => setEditEvent(e)} data-testid={`edit-attach-${e.id}`}>
                      Details
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editEvent && <UpdateDialog event={editEvent} onSaved={reload} onClose={() => setEditEvent(null)} />}
    </div>
  );
}
